import React from 'react';
import PropTypes from 'prop-types';
import { Handle, Position } from '@xyflow/react';
import ItemTooltip from '../../itemCard/ItemTooltip';
import IdChip from '../../common/IdChip';
import { H_SLOT_COUNT, V_SLOT_COUNT, STACK_MAX_SHOWN } from '../impactGraphLayout';

// Custom React Flow node for the impact graph (#182 rebuild). Every node
// carries several handles per side (was one per side — #182 polish round 2,
// item 9) so edges of DIFFERENT relationship types can leave/enter from
// distinct anchor points (their colours don't overlap at the node), while
// SAME-type edges deliberately share one point (PO preference): `H_SLOT_COUNT`
// bottom (source, family-tree + V-model lineage "out") and top (target,
// lineage "in") handles, `V_SLOT_COUNT` left (target) and right (source)
// handles for the V-model's verify "rung". `impactGraphLayout.js`'s
// `handleId()` hashes an edge's relationship type to a slot, so the SAME
// layout module and this component agree on slot counts (imported, not
// duplicated). Unused handles simply have no edge attached.
//
// Reuses ItemTooltip + IdChip exactly as the list view (ImpactTab.js) does, so
// the rich hover tooltip and the friendly-id/GUID display preference (#174)
// work identically here — no new tooltip component.
//
// Future extension point (per #182 discussion): a `data.heat`/`data.similarity`
// field could drive a red/green "semantic closeness" background fade later
// (e.g. `style={{ '--ig-heat': data.heat }}` plus a CSS rule) without
// restructuring this component.

// Evenly spread `count` handle anchor points along a side, inset a little
// from the corners (e.g. count=5 -> 15/32.5/50/67.5/85%) so fanned-out edges
// don't leave right at a node's corner.
function slotPercents(count) {
  const inset = 15;
  const span = 100 - inset * 2;
  return Array.from({ length: count }, (_, i) => (count === 1 ? 50 : inset + (span * i) / (count - 1)));
}

const BOTTOM_TOP_PCTS = slotPercents(H_SLOT_COUNT);
const LEFT_RIGHT_PCTS = slotPercents(V_SLOT_COUNT);

// `*_PCTS` are module-level constants (computed once from the fixed
// `H_SLOT_COUNT`/`V_SLOT_COUNT`) and never reordered/filtered at runtime, so each
// handle's own evenly-spaced percentage is a stable key — `id` (which react-flow
// actually keys edges by) is left unchanged.
const HANDLES = (
  <>
    {BOTTOM_TOP_PCTS.map((pct, i) => (
      <Handle key={`top-${pct}`} type="target" position={Position.Top} id={`top-${i}`} style={{ left: `${pct}%` }} />
    ))}
    {BOTTOM_TOP_PCTS.map((pct, i) => (
      <Handle key={`bottom-${pct}`} type="source" position={Position.Bottom} id={`bottom-${i}`} style={{ left: `${pct}%` }} />
    ))}
    {LEFT_RIGHT_PCTS.map((pct, i) => (
      <Handle key={`left-${pct}`} type="target" position={Position.Left} id={`left-${i}`} style={{ top: `${pct}%` }} />
    ))}
    {LEFT_RIGHT_PCTS.map((pct, i) => (
      <Handle key={`right-${pct}`} type="source" position={Position.Right} id={`right-${i}`} style={{ top: `${pct}%` }} />
    ))}
  </>
);

function ImpactGraphNode({ id, data }) {
  const { itemId, displayId, title, typeId, status, kind, linkType, showTitles, showIds, showHumanIds, color, members } = data;

  if (kind === 'verify-more') {
    return (
      <div className="ig-node ig-node--more" data-testid={`graph-node-more-${id}`}>
        {HANDLES}
        {title}
      </div>
    );
  }

  // #182 follow-up (2026-07-08, item 2): a "stack" collapses several same-type
  // LEAF siblings (impactGraphLayout.js's `collapseLeafStacks`) into one node
  // with a single connection line — but each member keeps its OWN
  // `ItemTooltip` (so hover/click/deep-link still work per-item, not just for
  // the group), unlike `verify-more`'s plain "+N" overflow label which has no
  // per-item identity to preserve.
  if (kind === 'stack') {
    const shown = members.slice(0, STACK_MAX_SHOWN);
    const overflow = members.length - shown.length;
    return (
      <div
        className="ig-node ig-node--stack"
        style={{ '--ig-accent': color }}
        data-testid={`graph-node-${id}`}
        data-link-type={linkType || undefined}
        data-stack-count={members.length}
      >
        {HANDLES}
        {shown.map((m) => (
          <div className="ig-stack-row" key={m.id}>
            <ItemTooltip item={{ id: m.id, display_id: m.display_id, title: m.title, type: m.type_id, status: m.status }}>
              {showIds && <IdChip id={m.id} displayId={m.display_id} showHumanIds={showHumanIds} />}
              {showTitles && <span className="ig-stack-row-title">{m.title || '—'}</span>}
            </ItemTooltip>
          </div>
        ))}
        {overflow > 0 && <span className="ig-stack-more">+{overflow} more</span>}
      </div>
    );
  }

  return (
    <div
      className={`ig-node ig-node--${kind}`}
      style={{ '--ig-accent': color }}
      data-testid={`graph-node-${id}`}
      data-link-type={linkType || undefined}
    >
      {HANDLES}
      <ItemTooltip item={{ id: itemId, display_id: displayId, title, type: typeId, status }}>
        <span className="ig-node-inner">
          {/* #182 polish round 2 (item 6): title and id are now two
              INDEPENDENT toggles (was one `showTitles` switch) — either, both,
              or neither may be visible. */}
          {showIds && <IdChip id={itemId} displayId={displayId} showHumanIds={showHumanIds} />}
          {showTitles && <span className="ig-node-title">{title || '—'}</span>}
        </span>
      </ItemTooltip>
    </div>
  );
}

ImpactGraphNode.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    itemId: PropTypes.string,
    displayId: PropTypes.string,
    title: PropTypes.string,
    typeId: PropTypes.string,
    status: PropTypes.string,
    kind: PropTypes.oneOf(['root', 'ancestor', 'descendant', 'verify', 'verify-more', 'stack']).isRequired,
    linkType: PropTypes.string,
    members: PropTypes.arrayOf(PropTypes.object),
    showTitles: PropTypes.bool,
    showIds: PropTypes.bool,
    showHumanIds: PropTypes.bool,
    color: PropTypes.string,
  }).isRequired,
};

export default React.memo(ImpactGraphNode);
